import { apiClient, api } from '@/lib/api'
import type { User } from '@/types'

export interface MyOrganization {
  orgId: string
  name: string
  slug: string
  role: string
  isPrimary?: boolean
}

export interface LoginRequest {
  email: string
  password: string
  rememberMe?: boolean
}

export interface LoginResponse {
  accessToken: string
  user: User
}

export interface RegisterRequest {
  orgName: string
  name: string
  email: string
  password: string
}

/** `GET /auth/reset-password/validate`: whether a reset / invitation link still works. */
export type ResetTokenValidation =
  | { valid: false }
  | {
      valid: true
      /** The account the link sets the password for. */
      email: string
      /** true = an invitation (the account is still `invited`), false = a password reset. */
      invite: boolean
    }

/** `POST /auth/reset-password`. `email`/`canSignIn` are absent only from an older API. */
export interface ResetPasswordResult {
  message: string
  email?: string
  /**
   * false when the password was saved but the account can't sign in yet — an invitation
   * the seat check refused, so the owner has to activate it.
   */
  canSignIn?: boolean
}

/** `GET /auth/lockout-info`: who to ask when the subscription has locked this user out. */
export interface LockoutInfo {
  orgName: string | null
  /** The org's active owner; null when it has none. */
  owner: { name: string; email: string } | null
  /** Free seats on the org's plan. */
  freeSeats: number | null
}

// These auth endpoints answer with a bare body (no `{ data }` envelope); tolerate either.
function unwrapBody<T>(body: any): T {
  return (body && typeof body === 'object' && 'data' in body ? body.data : body) as T
}

export const authService = {
  async login(email: string, password: string, rememberMe?: boolean): Promise<LoginResponse> {
    const response = await apiClient.post<LoginResponse>('/auth/login', {
      email,
      password,
      rememberMe
    })
    return response.data
  },

  async demoLogin(): Promise<LoginResponse> {
    const response = await apiClient.post<LoginResponse>('/auth/demo-login', {})
    return response.data
  },

  async register(data: RegisterRequest): Promise<LoginResponse> {
    const response = await apiClient.post<LoginResponse>('/auth/register', data)
    return response.data
  },

  async logout(): Promise<void> {
    await apiClient.post('/auth/logout')
  },

  async getCurrentUser(): Promise<User> {
    const response = await apiClient.get<User>('/auth/me')
    return response.data
  },

  // Multi-org: the orgs the current user belongs to (for the switcher).
  async listMyOrganizations(): Promise<MyOrganization[]> {
    const response = await apiClient.get<MyOrganization[]>('/auth/organizations')
    return response.data
  },

  // Create an additional organization owned by the current user.
  async createOrganization(name: string): Promise<MyOrganization> {
    const response = await apiClient.post<MyOrganization>('/auth/organizations', { name })
    return response.data
  },

  // Leave an org you belong to. Returns the org to fall back to, if any.
  async leaveOrganization(orgId: string): Promise<{ message: string; newActiveOrgId?: string }> {
    const response = await apiClient.post<{ message: string; newActiveOrgId?: string }>(
      `/auth/organizations/${orgId}/leave`,
    )
    return response.data
  },

  // Transfer ownership of an org to another member.
  async transferOwnership(orgId: string, newOwnerUserId: string): Promise<{ message: string }> {
    const response = await apiClient.post<{ message: string }>(
      `/auth/organizations/${orgId}/transfer-ownership`,
      { newOwnerUserId },
    )
    return response.data
  },

  // Delete an org and all its data (owner-only, non-personal). DELETE carries a
  // confirmation body, so it uses the raw axios instance.
  async deleteOrganization(orgId: string): Promise<{ message: string }> {
    const response = await api.delete(`/auth/organizations/${orgId}`, {
      data: { confirmation: 'delete' },
    })
    return response.data?.data ?? response.data
  },

  async forgotPassword(email: string): Promise<{ message: string }> {
    const response = await apiClient.post<{ message: string }>('/auth/forgot-password', { email })
    return response.data
  },

  async resetPassword(token: string, newPassword: string, confirmPassword: string): Promise<ResetPasswordResult> {
    const response = await api.post('/auth/reset-password', {
      token,
      newPassword,
      confirmPassword
    })
    return unwrapBody<ResetPasswordResult>(response.data)
  },

  // Is this reset / invitation link still good, and for which account? Never throws for a
  // bad token — that's `{ valid: false }`.
  async validateResetToken(token: string): Promise<ResetTokenValidation> {
    const response = await api.get('/auth/reset-password/validate', { params: { token } })
    return unwrapBody<ResetTokenValidation>(response.data)
  },

  // Owner and plan details for the locked-out screen (the active org, per x-org-id).
  async lockoutInfo(): Promise<LockoutInfo> {
    const response = await api.get('/auth/lockout-info')
    return unwrapBody<LockoutInfo>(response.data)
  }
}